<script setup>
import { Tag } from 'primevue';
import { computed } from 'vue';
import { useLayout } from '../layouts/composables/layout';

const { isDarkTheme } = useLayout();

const props = defineProps({
    // Individual notification object
    notification: {
        type: Object,
        default: () => null
    },
    // Array of notifications
    notifications: {
        type: Array,
        default: () => []
    },
    // Notification type for styling (error, warning, info, success)
    type: {
        type: String,
        default: 'info',
        validator: (value) => ['error', 'warning', 'info', 'success'].includes(value)
    },
    // Show/hide the notification
    show: {
        type: Boolean,
        default: true
    }
});

// Get notifications to display (either single notification or array)
const notificationsToShow = computed(() => {
    if (props.notification) {
        return [props.notification];
    }
    return props.notifications;
});

// Type-based styling configurations
const typeConfigs = {
    error: {
        bgLight: 'bg-red-50',
        bgDark: 'bg-red-900/20',
        borderLight: 'border-red-200',
        borderDark: 'border-red-400',
        iconLight: 'text-red-500',
        iconDark: 'text-red-400',
        textLight: 'text-red-700',
        textDark: 'text-red-300',
        defaultIcon: 'pi-exclamation-triangle'
    },
    warning: {
        bgLight: 'bg-yellow-50',
        bgDark: 'bg-yellow-900/20',
        borderLight: 'border-yellow-200',
        borderDark: 'border-yellow-400',
        iconLight: 'text-yellow-500',
        iconDark: 'text-yellow-400',
        textLight: 'text-yellow-700',
        textDark: 'text-yellow-300',
        defaultIcon: 'pi-exclamation-triangle'
    },
    info: {
        bgLight: 'bg-blue-50',
        bgDark: 'bg-blue-900/20',
        borderLight: 'border-blue-200',
        borderDark: 'border-blue-400',
        iconLight: 'text-blue-500',
        iconDark: 'text-blue-400',
        textLight: 'text-blue-700',
        textDark: 'text-blue-300',
        defaultIcon: 'pi-info-circle'
    },
    success: {
        bgLight: 'bg-green-50',
        bgDark: 'bg-green-900/20',
        borderLight: 'border-green-200',
        borderDark: 'border-green-400',
        iconLight: 'text-green-500',
        iconDark: 'text-green-400',
        textLight: 'text-green-700',
        textDark: 'text-green-300',
        defaultIcon: 'pi-check-circle'
    }
};

// Dynamic styling based on theme and type
const cardClasses = computed(() => {
    const config = typeConfigs[props.type];
    const baseClasses = 'flex items-start gap-3 p-3 rounded-lg border';

    if (isDarkTheme.value) {
        return `${baseClasses} ${config.bgDark} ${config.borderDark}`;
    }
    return `${baseClasses} ${config.bgLight} ${config.borderLight}`;
});

const iconClasses = computed(() => {
    const config = typeConfigs[props.type];
    if (isDarkTheme.value) {
        return config.iconDark;
    }
    return config.iconLight;
});

const textClasses = computed(() => {
    const config = typeConfigs[props.type];
    if (isDarkTheme.value) {
        return config.textDark;
    }
    return config.textLight;
});

const descriptionClasses = computed(() => {
    if (isDarkTheme.value) {
        return 'text-sm text-gray-300';
    }
    return 'text-sm text-gray-600';
});

// Get icon for notification
const getIcon = (notification) => {
    return notification.icon || typeConfigs[props.type].defaultIcon;
};
</script>

<template>
    <div v-if="show && notificationsToShow.length > 0" class="space-y-3">
        <div v-for="(notification, index) in notificationsToShow" :key="index" :class="cardClasses">
            <i :class="`pi ${getIcon(notification)} ${iconClasses} text-lg flex-shrink-0 mt-0.5`"></i>

            <div class="flex-1 min-w-0">
                <!-- Title and Tag Row -->
                <div v-if="notification.title || notification.tag" class="flex items-center gap-2 mb-1">
                    <span v-if="notification.title" :class="`text-sm font-medium ${textClasses}`">
                        {{ notification.title }}
                    </span>
                    <Tag v-if="notification.tag" :value="notification.tag.label || notification.tag"
                        :severity="notification.tag.severity || 'secondary'" />
                </div>

                <!-- Message -->
                <div v-if="notification.message" :class="`text-sm font-medium ${textClasses}`">
                    {{ notification.message }}
                </div>

                <!-- Description -->
                <div v-if="notification.description" :class="descriptionClasses">
                    {{ notification.description }}
                </div>
            </div>
        </div>
    </div>
</template>
